import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { DownOutlined, PlusOutlined, EllipsisOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc'
import { arrayMoveImmutable } from 'array-move'
import QuestionCard from './QuestionCard'
import {
  FIELD_STATUS,
  RESPONSE_TYPES,
  VALIDATION_RULES,
  getSectionsByType,
  sectionsToFormik,
  formikToSections,
} from './templates'
import {
  FormBuilderContainer,
  LeftPanel,
  RightPanel,
  StatusLegendBar,
  StatusLegendItem,
  StatusDot,
  NotesBox,
  NotesTitle,
  SectionBlock,
  SectionHeaderRow,
  FieldItemRow,
  DragHandle,
  FieldName,
  StatusBadgeGroup,
  StatusBadge,
  MoreButton,
  PreviewCard,
  FormTitleInput,
  PreviewSectionWrapper,
  PreviewSectionHeader,
  PreviewSectionTitle,
  ClearButton,
  PreviewFieldWrapper,
  PreviewFieldLabel,
  PreviewInput,
  PreviewSelect,
  PreviewRadioGroup,
  PreviewRadioItem,
  PreviewRadio,
  CustomQuestionsWrapper,
  CustomQuestionsTitle,
  AddCustomButton,
  Divider,
  SortableGlobalStyle,
} from './style'

// Sortable drag handle
const SortableDragHandle = sortableHandle(() => (
  <DragHandle>
    <span /><span /><span /><span /><span /><span />
  </DragHandle>
))

// Sortable field item for section fields
const SortableFieldItem = sortableElement(({ field, sectionIdx, fieldIdx, onStatusChange, isFormDisabled }) => {
  const isRequiredOnly = ['q1', 'q2', 'q5', 'q7', 'oc_b2', 'oc_b3', 'oc_b6', 'oc_b7', 'ex_b2', 'ex_b3', 'ex_b6', 'ex_b7'].includes(field.id)

  return (
    <FieldItemRow>
      <SortableDragHandle disabled={isFormDisabled} />
      <Tooltip title={field.question}>
        <FieldName>{field.question}</FieldName>
      </Tooltip>
      <StatusBadgeGroup>
        <StatusBadge
          badgeType="required"
          active={field.status === 'required'}
          onClick={() => !isRequiredOnly && !isFormDisabled && onStatusChange(sectionIdx, fieldIdx, 'required')}
          disabled={isFormDisabled}
        >
          Req
        </StatusBadge>
        <StatusBadge
          badgeType="optional"
          active={field.status === 'optional'}
          onClick={() => !isRequiredOnly && !isFormDisabled && onStatusChange(sectionIdx, fieldIdx, 'optional')}
          disabled={isRequiredOnly || isFormDisabled}
        >
          Opt
        </StatusBadge>
        <StatusBadge
          badgeType="off"
          active={field.status === 'off'}
          onClick={() => !isRequiredOnly && !isFormDisabled && onStatusChange(sectionIdx, fieldIdx, 'off')}
          disabled={isRequiredOnly || isFormDisabled}
        >
          Off
        </StatusBadge>
      </StatusBadgeGroup>
      {/* <MoreButton>
        <EllipsisOutlined />
      </MoreButton> */}
    </FieldItemRow>
  )
})

// Sortable container for section fields
const SortableFieldList = sortableContainer(({ items, sectionIdx, onStatusChange, isFormDisabled }) => (
  <div>
    {items.map((field, fIdx) => (
      <SortableFieldItem
        key={field.id}
        index={fIdx}
        field={field}
        sectionIdx={sectionIdx}
        fieldIdx={fIdx}
        onStatusChange={onStatusChange}
        isFormDisabled={isFormDisabled}
      />
    ))}
  </div>
))

// Sortable custom question item
const SortableCustomItem = sortableElement(({ question, questionIndex, onRequiredChange, isFormDisabled }) => (
  <FieldItemRow>
    <SortableDragHandle disabled={isFormDisabled} />
    <Tooltip title={question.question || '(Untitled)'}>
      <FieldName>{question.question || '(Untitled)'}</FieldName>
    </Tooltip>
    <StatusBadgeGroup>
      <StatusBadge
        badgeType="required"
        active={question.required}
        onClick={() => !isFormDisabled && onRequiredChange(questionIndex, true)}
        disabled={isFormDisabled}
      >
        Req
      </StatusBadge>
      <StatusBadge
        badgeType="optional"
        active={!question.required}
        onClick={() => !isFormDisabled && onRequiredChange(questionIndex, false)}
        disabled={isFormDisabled}
      >
        Opt
      </StatusBadge>
    </StatusBadgeGroup>
    {/* <MoreButton>
        <EllipsisOutlined />
      </MoreButton> */}
  </FieldItemRow>
))

// Sortable container for custom questions
const SortableCustomList = sortableContainer(({ items, onRequiredChange, isFormDisabled }) => (
  <div>
    {items.map((q, idx) => (
      <SortableCustomItem
        key={q.id}
        index={idx}
        questionIndex={idx}
        question={q}
        onRequiredChange={onRequiredChange}
        isFormDisabled={isFormDisabled}
      />
    ))}
  </div>
))

const ApplicationForm = ({ roleData, setFieldValue, selectedCourses, onValidationTrigger, isEditMode = false, rePublishedPublishDisabled = false }) => {
  const { values } = useFormikContext()

  const eligibilityCriteria = values?.elgibilty?.eligibilityCriteria || []
  const templateType = getTemplateType(eligibilityCriteria, roleData, values, selectedCourses)
  // Allow editing the application/Tally form questions any number of times,
  // even after the role is republished. Backend (corporateJobRoleHandler) accepts
  // applicationFormTemplate updates on 3rd+ edit and updates the Tally form in-place
  // (same form URL preserved), so repeated edits are safe.
  const isFormDisabled = false

  const [sections, setSections] = useState(() => getSectionsByType(templateType))
  const [customQuestions, setCustomQuestions] = useState([])
  const [formTitle, setFormTitle] = useState('Application Form')
  const [collapsedSections, setCollapsedSections] = useState({})
  const [showValidation, setShowValidation] = useState(false)
  const rightPanelRef = useRef(null)
  const leftPanelRef = useRef(null)

  const syncToFormik = useCallback(
    (updatedSections, updatedCustom) => {
      const mandatoryQuestions = sectionsToFormik(updatedSections)
      setFieldValue('applicationForm', {
        templateType: templateType || 'DIPLOMA',
        mandatoryQuestions,
        customQuestions: updatedCustom,
      })
    },
    [setFieldValue, templateType]
  )

  useEffect(() => {
    const saved = values?.applicationForm
    if (saved?.mandatoryQuestions?.length > 0 && saved?.templateType === templateType) {
      setSections(formikToSections(saved.mandatoryQuestions, templateType))
    } else {
      const defaultSections = getSectionsByType(templateType)
      setSections(defaultSections)
      syncToFormik(defaultSections, templateType === saved?.templateType ? (saved?.customQuestions || []) : [])
      if (templateType !== saved?.templateType) setCustomQuestions([])
    }
    if (saved?.customQuestions?.length > 0 && saved?.templateType === templateType) {
      setCustomQuestions(saved.customQuestions)
    }
  }, [templateType])

  // Listen for validation trigger
  useEffect(() => {
    const handleTrigger = () => {
      setShowValidation(true)
    }

    window.addEventListener('triggerValidation', handleTrigger)
    return () => window.removeEventListener('triggerValidation', handleTrigger)
  }, [])

  const handleFieldStatusChange = (sectionIdx, fieldIdx, newStatus) => {
    const updated = sections.map((s, si) => {
      if (si !== sectionIdx) return s
      return {
        ...s,
        fields: s.fields.map((f, fi) => {
          if (fi !== fieldIdx) return f
          return { ...f, status: newStatus }
        }),
      }
    })
    setSections(updated)
    syncToFormik(updated, customQuestions)
  }

  const handleClearSection = (sectionIdx) => {
    const updated = sections.map((s, si) => {
      if (si !== sectionIdx) return s
      return {
        ...s,
        fields: s.fields.map(f => ({ ...f, status: FIELD_STATUS.OFF })),
      }
    })
    setSections(updated)
    syncToFormik(updated, customQuestions)
  }

  const handleResetSection = (sectionIdx) => {
    const defaultSections = getSectionsByType(templateType)
    const updated = sections.map((s, si) => {
      if (si !== sectionIdx) return s
      return { ...defaultSections[si] }
    })
    setSections(updated)
    syncToFormik(updated, customQuestions)
  }

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }))
  }

  const handleFieldSortEnd = (sectionIdx, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    const updated = sections.map((s, si) => {
      if (si !== sectionIdx) return s
      return { ...s, fields: arrayMoveImmutable(s.fields, oldIndex, newIndex) }
    })
    setSections(updated)
    syncToFormik(updated, customQuestions)
  }

  const handleCustomSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    const updated = arrayMoveImmutable(customQuestions, oldIndex, newIndex)
    setCustomQuestions(updated)
    syncToFormik(sections, updated)
  }

  const handleAddCustomQuestion = () => {
    if (isFormDisabled) return
    const newQuestion = {
      id: `cq_${Date.now()}`,
      question: '',
      responseType: 'SHORT_ANSWER',
      required: true,
      options: [],
    }
    const updated = [...customQuestions, newQuestion]
    setCustomQuestions(updated)
    syncToFormik(sections, updated)

    // Scroll right panel to bottom
    setTimeout(() => {
      if (leftPanelRef.current) {
        leftPanelRef.current.scrollTo({
          top: leftPanelRef.current.scrollHeight,
          behavior: 'smooth'
        })
      }

      if (rightPanelRef.current) {
        rightPanelRef.current.scrollTo({
          top: rightPanelRef.current.scrollHeight,
          behavior: 'smooth'
        })
      }
    }, 100)
  }

  const handleUpdateCustom = (index, updatedQuestion) => {
    const updated = [...customQuestions]
    updated[index] = updatedQuestion
    setCustomQuestions(updated)
    syncToFormik(sections, updated)
  }

  const handleDeleteCustom = (index) => {
    const updated = customQuestions.filter((_, i) => i !== index)
    setCustomQuestions(updated)
    syncToFormik(sections, updated)
  }

  const handleDuplicateCustom = (index) => {
    const original = customQuestions[index]
    const duplicate = {
      ...original,
      id: `cq_${Date.now()}`,
      question: `${original.question} (Copy)`,
    }
    const updated = [...customQuestions]
    updated.splice(index + 1, 0, duplicate)
    setCustomQuestions(updated)
    syncToFormik(sections, updated)
  }

  const handleRequiredChange = (index, required) => {
    const updated = [...customQuestions]
    updated[index] = { ...updated[index], required }
    setCustomQuestions(updated)
    syncToFormik(sections, updated)
  }

  return (
    <FormBuilderContainer>
      <SortableGlobalStyle />
      {/* ====== LEFT PANEL - Field Configuration ====== */}
      <LeftPanel ref={leftPanelRef}>
        <StatusLegendBar>
          <StatusLegendItem>
            <StatusDot status="required" />
            Required
          </StatusLegendItem>
          <StatusLegendItem>
            <StatusDot status="optional" />
            Optional
          </StatusLegendItem>
          <StatusLegendItem>
            <StatusDot status="off" />
            Off
          </StatusLegendItem>
        </StatusLegendBar>

        <NotesBox>
          <NotesTitle>Notes:</NotesTitle>
          Candidate&apos;s fields are listed below. Set each field as Required, Optional, or Off to control visibility in the form.
        </NotesBox>

        {sections.map((section, sIdx) => (
          <SectionBlock key={section.id}>
            <SectionHeaderRow
              collapsed={collapsedSections[section.id]}
              onClick={() => !isFormDisabled && toggleSectionCollapse(section.id)}
              style={{ cursor: isFormDisabled ? 'default' : 'pointer' }}
            >
              <span>{section.title}</span>
              <DownOutlined style={{ fontSize: 11, color: '#676E83' }} />
            </SectionHeaderRow>

            {!collapsedSections[section.id] && (
              <SortableFieldList
                items={section.fields}
                sectionIdx={sIdx}
                onSortEnd={(sortData) => !isFormDisabled && handleFieldSortEnd(sIdx, sortData)}
                onStatusChange={handleFieldStatusChange}
                isFormDisabled={isFormDisabled}
                useDragHandle={!isFormDisabled}
                lockAxis="y"
                helperClass="sortable-helper"
              />
            )}
          </SectionBlock>
        ))}

        {/* Custom Questions in left panel */}
        <SectionBlock>
          <SectionHeaderRow
            collapsed={collapsedSections['custom']}
            onClick={() => !isFormDisabled && toggleSectionCollapse('custom')}
            style={{ cursor: isFormDisabled ? 'default' : 'pointer' }}
          >
            <span>Custom Questions</span>
            <DownOutlined style={{ fontSize: 11, color: '#676E83' }} />
          </SectionHeaderRow>
          {!collapsedSections['custom'] && (
            <SortableCustomList
              items={customQuestions}
              onRequiredChange={handleRequiredChange}
              isFormDisabled={isFormDisabled}
              onSortEnd={(sortData) => !isFormDisabled && handleCustomSortEnd(sortData)}
              useDragHandle={!isFormDisabled}
              lockAxis="y"
              helperClass="sortable-helper"
            />
          )}
        </SectionBlock>

        <AddCustomButton onClick={handleAddCustomQuestion} disabled={isFormDisabled}>
          <PlusOutlined style={{ fontSize: 13, paddingLeft: '15px' }} />
          Add Custom Question
        </AddCustomButton>
      </LeftPanel>

      {/* ====== RIGHT PANEL - Live Form Preview ====== */}
      <RightPanel>
        <div ref={rightPanelRef} style={{ height: '100%', overflowY: 'auto' }}>
          <PreviewCard>
            <FormTitleInput
              value={formTitle}
              disabled
              placeholder="Untitled Form"
            />

            {sections.map((section, sIdx) => {
              const activeFields = section.fields.filter(f => f.status !== 'off')
              if (activeFields.length === 0) return null

              return (
                <PreviewSectionWrapper key={section.id}>
                  <PreviewSectionHeader>
                    <PreviewSectionTitle>{section.title}</PreviewSectionTitle>
                  </PreviewSectionHeader>

                  {activeFields.map(field => (
                    <PreviewFieldWrapper key={field.id}>
                      <PreviewFieldLabel isRequired={field.status === 'required'}>
                        {field.question}
                      </PreviewFieldLabel>
                      <FieldPreviewInput field={field} />
                    </PreviewFieldWrapper>
                  ))}
                </PreviewSectionWrapper>
              )
            })}

            {/* Custom Questions Preview */}
            {customQuestions.length > 0 && (
              <CustomQuestionsWrapper>
                <CustomQuestionsTitle>Custom Questions</CustomQuestionsTitle>
                {customQuestions.map((q, idx) => (
                  <QuestionCard
                    key={q.id || idx}
                    question={q}
                    index={idx}
                    onUpdate={(updated) => !isFormDisabled && handleUpdateCustom(idx, updated)}
                    onDelete={() => !isFormDisabled && handleDeleteCustom(idx)}
                    onDuplicate={() => !isFormDisabled && handleDuplicateCustom(idx)}
                    isCustom={true}
                    showValidation={showValidation}
                    isFormDisabled={isFormDisabled}
                  />
                ))}
                <AddCustomButton onClick={handleAddCustomQuestion} disabled={isFormDisabled}>
                  <PlusOutlined style={{ fontSize: 13, paddingLeft: '15px' }} />
                  Add Custom Question
                </AddCustomButton>
              </CustomQuestionsWrapper>
            )}
          </PreviewCard>
        </div>
      </RightPanel>
    </FormBuilderContainer>
  )
}

// Renders the appropriate preview input based on field type
const FieldPreviewInput = ({ field }) => {
  const validationRule = field.validation ? VALIDATION_RULES[field.validation] : null
  const placeholder = field.placeholder || validationRule?.placeholder || ''

  switch (field.responseType) {
    case 'DROPDOWN': {
      const preview = (field.options && field.options.length > 0)
        ? (field.options.length <= 4 ? field.options.join(' / ') : `${field.options.slice(0, 3).join(', ')} … (${field.options.length} options)`)
        : 'Select...'
      return <PreviewSelect title={preview}>{placeholder || preview}</PreviewSelect>
    }

    case 'MULTIPLE_CHOICE':
      return (
        <PreviewRadioGroup>
          {(field.options || []).map((opt, i) => (
            <PreviewRadioItem key={i}>
              <PreviewRadio />
              {opt}
            </PreviewRadioItem>
          ))}
        </PreviewRadioGroup>
      )

    case 'CHECKBOXES':
      return (
        <PreviewRadioGroup>
          {(field.options || []).map((opt, i) => (
            <PreviewRadioItem key={i}>
              <PreviewRadio type="checkbox" />
              {opt}
            </PreviewRadioItem>
          ))}
        </PreviewRadioGroup>
      )

    case 'FILE_UPLOAD':
      return (
        <PreviewInput style={{ background: '#F8F9FB', border: '1px dashed #D0D5DD', textAlign: 'center', color: '#676E83', cursor: 'pointer' }}>
          Click to upload file
        </PreviewInput>
      )

    case 'DATE':
      return <PreviewInput>{placeholder || 'dd/mm/yyyy'}</PreviewInput>

    case 'MONTH_YEAR':
      return <PreviewInput>{placeholder || 'MMM YYYY'}</PreviewInput>

    case 'PARAGRAPH':
      return <PreviewInput style={{ minHeight: 72 }}>{placeholder}</PreviewInput>

    case 'SHORT_ANSWER':
    default:
      return <PreviewInput>{placeholder}</PreviewInput>
  }
}

// Helper: determine template type from eligibility criteria and hiring type
// Check if a degree short form indicates ITI
function isITIShortForm(shortForm) {
  return shortForm && shortForm.toUpperCase().trim() === 'ITI'
}

function getTemplateType(eligibilityCriteria, roleData, values, selectedCourses) {
  // Determine if it's Off-campus or On-campus
  const isForAll = values?.jobDetails?.isForAll ?? roleData?.isForAll
  const isOffCampus = isForAll === true
  const isOnCampus = !isOffCampus

  // Get experience level
  const jobTypeLevels = values?.jobDetails?.jobTypeLevels || roleData?.jobTypeLevels || []
  const experienceLevel = jobTypeLevels[0]

  // Check education selection (ITI vs Diploma)
  let hasITI = false
  let hasNonITIDiploma = false

  // Primary source: selectedCourses (real-time selection from QualificationSelectionBubble)
  if (Array.isArray(selectedCourses) && selectedCourses.some(arr => arr?.length > 0)) {
    for (const group of selectedCourses) {
      if (!Array.isArray(group)) continue
      for (const item of group) {
        if (!item?.isSelected && !item?.isAllDegree) continue
        const sf = item?.degreeShortForm || item?.shortForm || ''
        if (isITIShortForm(sf)) {
          hasITI = true
        } else if ((item?.degreeType || item?.type || '').toUpperCase() === 'DIPLOMA') {
          hasNonITIDiploma = true
        }
      }
    }
  }

  // Fallback: check formik eligibilityCriteria
  if (!hasITI && !hasNonITIDiploma && Array.isArray(eligibilityCriteria)) {
    for (const group of eligibilityCriteria) {
      const items = Array.isArray(group) ? group : [group]
      for (const item of items) {
        const sf = item?.degreeShortForm || ''
        const dt = (item?.degreeType || '').toUpperCase()
        if (isITIShortForm(sf)) {
          hasITI = true
        } else if (dt === 'DIPLOMA' || dt === 'UG_DIPLOMA') {
          hasNonITIDiploma = true
        }
      }
    }
  }

  // Determine template based on education selection first, then experience level
  if (hasITI && !hasNonITIDiploma) {
    // Only ITI selected - show ITI form for all experience levels
    return 'ITI'
  } else if (hasNonITIDiploma && !hasITI) {
    // Only Diploma selected - show Diploma form for all experience levels
    return 'DIPLOMA'
  } else if (hasITI && hasNonITIDiploma) {
    // Both ITI and Diploma selected - prioritize Diploma form
    return 'DIPLOMA'
  } else {
    // No specific education selection - determine based on campus type and experience level
    if (isOffCampus) {
      if (experienceLevel === 'EXPERIENCED') {
        return 'EXPERIENCED'
      } else if (experienceLevel === 'BOTH') {
        return 'OFF_CAMPUS_BOTH'
      } else {
        return 'OFF_CAMPUS_FRESHER'
      }
    } else {
      // On-campus - default to Diploma form regardless of experience level
      return 'DIPLOMA'
    }
  }
}

export default ApplicationForm
